(() => {
  const reduceMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches || document.body.classList.contains('nora-motion-off');

  /* iOS Safari jumps to a checked Atlas Bundler radio (or another focused control)
     after reload, which lands the page on the buy box / ingredients accordion. */
  if (!window.location.hash) {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    const stayTop = () => {
      const active = document.activeElement;
      if (active && active.matches && active.matches('input[type="radio"], input[type="checkbox"], [role="tab"]')) {
        active.blur();
      }
      if (window.scrollY > 0) window.scrollTo(0, 0);
    };
    stayTop();
    window.addEventListener('load', stayTop, { once: true });
    let cancelled = false;
    const cancel = () => { cancelled = true; };
    window.addEventListener('touchstart', cancel, { once: true, passive: true });
    window.addEventListener('wheel', cancel, { once: true, passive: true });
    [100, 300, 700, 1200].forEach((ms) => {
      window.setTimeout(() => { if (!cancelled && !window.location.hash) stayTop(); }, ms);
    });
  }

  const getForm = (sectionId) => document.getElementById(`product-form-${sectionId}`);
  const getBuy = (sectionId) => document.querySelector(`[data-nora-buy][data-section="${sectionId}"]`);

  /* Keeps the main + sticky button labels in sync with the purchase mode.
     Atlas VariantSelects rewrites `[name="add"] > span` with a generic label on every
     variant change, so this runs again after each variant change. */
  function syncLabels(sectionId) {
    const buy = getBuy(sectionId);
    if (!buy) return;
    const mode = buy.dataset.mode || 'onetime';
    const label = mode === 'subscribe' ? buy.dataset.subscribeLabel : buy.dataset.onetimeLabel;
    const main = document.getElementById(`ProductSubmitButton-${sectionId}`);
    if (main && !main.disabled) {
      const span = main.querySelector('[data-nora-atc-label]');
      if (span) span.textContent = label;
    }
    const sticky = document.querySelector(`nora-sticky-atc[data-section="${sectionId}"]`);
    if (sticky) sticky.sync();
  }

  document.addEventListener('change', (e) => {
    const input = e.target;
    if (!input || input.name !== 'id' || !input.form || !input.form.id.startsWith('product-form-')) return;
    const sectionId = input.form.id.replace('product-form-', '');
    setTimeout(() => syncLabels(sectionId), 0);
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const sectionId = event && event.data && event.data.sectionId;
      if (!sectionId) return;
      const options = document.querySelector(`nora-purchase-options[data-section="${sectionId}"]`);
      if (options) options.update();
      syncLabels(sectionId);
    });
  }

  /* ---------------------------------------------------------------- Gallery */
  class NoraGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-nora-gallery-viewport]');
      this.slides = Array.from(this.querySelectorAll('.nora-gallery__slide'));
      this.thumbs = Array.from(this.querySelectorAll('[data-nora-gallery-thumb]'));
      this.dots = Array.from(this.querySelectorAll('.nora-gallery__dot'));
      if (this.slides.length < 2 || !this.viewport) return;

      this.thumbs.forEach((thumb) =>
        thumb.addEventListener('click', () => this.setActiveMedia(thumb.dataset.noraGalleryThumb))
      );
      const prev = this.querySelector('[data-nora-gallery-prev]');
      const next = this.querySelector('[data-nora-gallery-next]');
      if (prev) prev.addEventListener('click', () => this.go(-1));
      if (next) next.addEventListener('click', () => this.go(1));
      this.viewport.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(-1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(1); }
      });

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) this.markActive(this.slides.indexOf(entry.target));
          });
        },
        { root: this.viewport, threshold: [0.6] }
      );
      this.slides.forEach((slide) => this.observer.observe(slide));

      const active = this.slides.findIndex((s) => s.classList.contains('is-active'));
      if (active > 0) this.scrollToIndex(active, false);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    get index() {
      return Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
    }

    go(step) {
      const next = (this.index + step + this.slides.length) % this.slides.length;
      this.scrollToIndex(next, true);
    }

    scrollToIndex(index, smooth) {
      const slide = this.slides[index];
      if (!slide) return;
      this.viewport.scrollTo({ left: slide.offsetLeft, behavior: smooth && !reduceMotion() ? 'smooth' : 'auto' });
      this.markActive(index);
    }

    markActive(index) {
      if (index < 0) return;
      this.slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
      this.dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
      this.thumbs.forEach((t, i) => {
        const on = i === index;
        t.classList.toggle('is-active', on);
        if (on) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      this.slides.forEach((s, i) => {
        if (i !== index) s.querySelectorAll('video').forEach((v) => v.pause());
      });
    }

    /* Called by Atlas VariantSelects.updateMedia(): `${sectionId}-${mediaId}` */
    setActiveMedia(mediaId) {
      const index = this.slides.findIndex((s) => s.dataset.mediaId === mediaId);
      if (index < 0) return;
      this.scrollToIndex(index, true);
    }
  }
  if (!customElements.get('nora-gallery')) customElements.define('nora-gallery', NoraGallery);

  /* ------------------------------------------------------- Purchase options */
  class NoraPurchaseOptions extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.form = document.getElementById(this.dataset.formId);
      this.buy = getBuy(this.sectionId);
      this.planInput = this.querySelector('[data-nora-selling-plan]');
      this.planSelect = this.querySelector('[data-nora-plan-select]');
      this.radios = Array.from(this.querySelectorAll('.nora-purchase__input'));
      try {
        this.data = JSON.parse(this.querySelector('[data-nora-purchase-data]').textContent);
      } catch (e) {
        this.data = { variants: {}, plans: {} };
      }

      const params = new URLSearchParams(window.location.search);
      const requested = params.get('purchase');
      if (requested === 'subscribe' || requested === 'onetime') this.setMode(requested, false);

      this.radios.forEach((radio) => radio.addEventListener('change', () => this.update()));
      if (this.planSelect) this.planSelect.addEventListener('change', () => this.update());

      if (this.form) {
        this.onVariantInput = (e) => {
          if (e.target && e.target.name === 'id' && e.target.form === this.form) this.update();
        };
        this.form.addEventListener('change', this.onVariantInput);
        /* Final guard: exactly one selling_plan, and only when subscribing
           (also neutralises any widget a subscription app may inject). */
        this.onFormData = (e) => {
          e.formData.delete('selling_plan');
          const plan = this.currentPlanId();
          if (this.mode() === 'subscribe' && plan) e.formData.set('selling_plan', plan);
        };
        this.form.addEventListener('formdata', this.onFormData);
      }

      this.update();
    }

    disconnectedCallback() {
      if (this.form) {
        this.form.removeEventListener('change', this.onVariantInput);
        this.form.removeEventListener('formdata', this.onFormData);
      }
    }

    mode() {
      const checked = this.radios.find((r) => r.checked);
      return checked ? checked.value : 'subscribe';
    }

    setMode(value, notify = true) {
      const radio = this.radios.find((r) => r.value === value && !r.disabled);
      if (radio) radio.checked = true;
      if (notify) this.update();
    }

    currentPlanId() {
      return (this.planSelect && this.planSelect.value) || (this.planInput && this.planInput.value) || String(this.data.defaultPlan || '');
    }

    currentVariantId() {
      const input = this.form && this.form.querySelector('[name="id"]');
      return input ? String(input.value) : '';
    }

    update() {
      const variant = this.data.variants[this.currentVariantId()];
      const planId = this.currentPlanId();
      const alloc = variant && variant.allocations ? variant.allocations[planId] : null;
      const subRadio = this.radios.find((r) => r.value === 'subscribe');
      const requiresPlan = this.dataset.requiresPlan === 'true';

      if (subRadio) {
        subRadio.disabled = !alloc;
        if (!alloc && subRadio.checked && !requiresPlan) this.setMode('onetime', false);
      }

      const pack = this.buy && this.buy.querySelector('[data-gutly-pack][aria-pressed="true"]');
      const linePrice = pack && pack.getAttribute('data-line-price');
      const lineSub = pack && pack.getAttribute('data-line-sub');
      const lineCompare = pack && pack.getAttribute('data-line-compare');

      if (linePrice) {
        this.setText('[data-nora-onetime-price]', linePrice);
      } else if (variant) {
        this.setText('[data-nora-onetime-price]', variant.price);
        this.setCompare('[data-nora-onetime-compare]', variant.compare);
      }
      if (lineSub) {
        this.setText('[data-nora-sub-price]', lineSub);
        this.setCompare('[data-nora-sub-compare]', lineCompare);
      } else if (alloc) {
        this.setText('[data-nora-sub-price]', alloc.price);
        this.setCompare('[data-nora-sub-compare]', alloc.compare);
      }
      if (alloc) {
        const badge = this.querySelector('[data-nora-save-badge]');
        if (badge) {
          badge.classList.toggle('hidden', !(alloc.savePct > 0));
          if (alloc.savePct > 0 && window.noraStrings && window.noraStrings.savePercent) {
            badge.textContent = window.noraStrings.savePercent.replace('[percent]', alloc.savePct);
          }
        }
      }

      const desc = this.querySelector('[data-nora-plan-desc]');
      if (desc && this.data.plans) {
        const text = this.data.plans[planId];
        desc.textContent = text || '';
        desc.hidden = !text;
      }

      const mode = this.mode();
      if (this.planInput) {
        this.planInput.value = planId;
        this.planInput.disabled = mode !== 'subscribe';
      }
      this.querySelectorAll('.nora-purchase__option').forEach((opt) =>
        opt.classList.toggle('is-selected', opt.dataset.mode === mode)
      );
      if (this.buy) {
        this.buy.dataset.mode = mode;
        this.buy.dataset.subPrice = lineSub || (alloc ? alloc.price : '');
      }
      const hero = document.querySelector(`#price-${this.sectionId} .nora-price__current`);
      if (hero) {
        if (mode === 'subscribe' && (lineSub || alloc)) hero.textContent = lineSub || alloc.price;
        else if (linePrice || variant) hero.textContent = linePrice || variant.price;
      }
      syncLabels(this.sectionId);
    }

    setText(selector, value) {
      const el = this.querySelector(selector);
      if (el && value != null) el.textContent = value;
    }

    setCompare(selector, value) {
      const el = this.querySelector(selector);
      if (!el) return;
      el.classList.toggle('hidden', !value);
      if (value) el.textContent = value;
    }
  }
  if (!customElements.get('nora-purchase-options')) customElements.define('nora-purchase-options', NoraPurchaseOptions);

  /* --------------------------------------------------------------- Quantity */
  class NoraQuantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('[data-nora-qty]').forEach((btn) =>
        btn.addEventListener('click', () => this.step(btn.dataset.noraQty === 'plus' ? 1 : -1))
      );
      this.input.addEventListener('change', () => this.clamp());
      this.render();
    }

    get min() { return parseInt(this.dataset.min || '1', 10); }
    get max() { return this.dataset.max ? parseInt(this.dataset.max, 10) : Infinity; }
    get inc() { return parseInt(this.dataset.step || '1', 10); }

    step(dir) {
      const value = parseInt(this.input.value, 10) || this.min;
      this.input.value = Math.min(this.max, Math.max(this.min, value + dir * this.inc));
      this.render();
    }

    clamp() {
      const value = parseInt(this.input.value, 10);
      this.input.value = Number.isNaN(value) ? this.min : Math.min(this.max, Math.max(this.min, value));
      this.render();
    }

    render() {
      const value = parseInt(this.input.value, 10);
      const [minus, plus] = this.querySelectorAll('[data-nora-qty]');
      if (minus) minus.disabled = value <= this.min;
      if (plus) plus.disabled = value >= this.max;
    }
  }
  if (!customElements.get('nora-quantity')) customElements.define('nora-quantity', NoraQuantity);

  /* -------------------------------------------------------------- Sticky ATC */
  class NoraStickyAtc extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.target = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      this.priceEl = this.querySelector('[data-nora-sticky-price]');
      this.modeEl = this.querySelector('[data-nora-sticky-mode]');
      this.labelEl = this.querySelector('[data-nora-sticky-label]');
      this.button = this.querySelector('[data-nora-sticky-submit]');
      this.desktopQuery = window.matchMedia('(min-width: 990px)');
      if (!this.target) return;

      this.passed = false;
      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.toggle();
        },
        { threshold: 0 }
      );
      this.observer.observe(this.target);
      this.onResize = () => this.toggle();
      this.desktopQuery.addEventListener('change', this.onResize);
      this.sync();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.desktopQuery) this.desktopQuery.removeEventListener('change', this.onResize);
      this.setVisible(false);
    }

    toggle() {
      const allowed = !this.desktopQuery.matches || this.classList.contains('nora-sticky-atc--desktop');
      this.setVisible(this.passed && allowed);
    }

    setVisible(visible) {
      this.classList.toggle('is-visible', visible);
      if (visible) {
        this.removeAttribute('inert');
        this.setAttribute('aria-hidden', 'false');
        document.documentElement.style.setProperty('--nora-sticky-atc-h', `${this.offsetHeight}px`);
      } else {
        this.setAttribute('inert', '');
        this.setAttribute('aria-hidden', 'true');
        document.documentElement.style.setProperty('--nora-sticky-atc-h', '0px');
      }
      document.body.classList.toggle('nora-sticky-atc-visible', visible);
    }

    sync() {
      const buy = getBuy(this.sectionId);
      const mode = buy ? buy.dataset.mode : 'onetime';
      const subscribing = mode === 'subscribe' && buy && buy.dataset.subPrice;
      const current = document.querySelector(`#price-${this.sectionId} .nora-price__current`);

      if (this.priceEl) {
        if (subscribing) this.priceEl.textContent = buy.dataset.subPrice;
        else if (current) this.priceEl.textContent = current.textContent.trim();
      }
      if (this.modeEl) {
        const strings = window.noraStrings || {};
        this.modeEl.textContent = subscribing ? strings.subscribeMode || '' : '';
      }

      const main = this.target || document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      if (this.button && main) {
        this.button.disabled = main.disabled;
        if (this.labelEl) {
          const mainLabel = main.querySelector('[data-nora-atc-label]');
          if (mainLabel) this.labelEl.textContent = mainLabel.textContent.trim();
        }
      }
    }
  }
  if (!customElements.get('nora-sticky-atc')) customElements.define('nora-sticky-atc', NoraStickyAtc);
})();
